import { supabase } from "@/integrations/supabase/client";
import { WidgetStyleVisionPrompt } from "@/services/agent/vision/prompts/WidgetStyleVisionPrompt";

import type {
  EmbedStyle,
  WidgetBackgroundStyle,
  WidgetHeaderStyle,
  WidgetButtonStyle,
  WidgetActionButtonStyle,
  WidgetChatStyle,
} from "@/types/ui/branding";
import type { WidgetTemplateId } from "@/types/ui/widget-styles";

export interface ProposedTopLevelBrand {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  gradientEnabled?: boolean;
  gradientAngle?: number;
}

export interface ProposedEmbedStyle {
  buttonStyle?: "rounded" | "square" | "pill";
  widgetTemplate?: WidgetTemplateId;
  widgetBackgroundColor?: string;
  background?: WidgetBackgroundStyle;
  fullPageBackground?: WidgetBackgroundStyle;
  headerStyle?: WidgetHeaderStyle;
  headerLogoSize?: number;
  ctaButton?: WidgetButtonStyle;
  actionButtons?: WidgetActionButtonStyle;
  avatarSizeScale?: number;
  avatarShowLogoOverlay?: boolean;
  chatStyle?: WidgetChatStyle;
  hideWelcomeText?: boolean;
  hideWelcomeMessage?: boolean;
}

export interface ProposedWidgetStyle {
  topLevel: ProposedTopLevelBrand;
  embedStyle: ProposedEmbedStyle;
}

const ANALYSIS_TIMEOUT_MS = 25_000;

const TOP_LEVEL_KEYS = new Set(["primaryColor", "secondaryColor", "accentColor", "gradientEnabled", "gradientAngle"]);
const EMBED_STYLE_KEYS = new Set([
  "buttonStyle",
  "widgetTemplate",
  "widgetBackgroundColor",
  "background",
  "fullPageBackground",
  "headerStyle",
  "headerLogoSize",
  "ctaButton",
  "actionButtons",
  "avatarSizeScale",
  "avatarShowLogoOverlay",
  "chatStyle",
  "hideWelcomeText",
  "hideWelcomeMessage",
]);
const VALID_WIDGET_TEMPLATES = new Set<WidgetTemplateId>([
  "classic",
  "compact",
  "immersive",
  "pill-button",
  "side-tab",
  "banner",
  "glassmorphism",
  "dark-mode",
  "magic-v2",
]);
const VALID_BACKGROUND_TYPES = new Set(["solid", "gradient", "pattern"]);
const VALID_PATTERN_IDS = new Set(["dots", "grid", "diagonal-lines"]);

function sanitizeBackground(value: unknown): WidgetBackgroundStyle | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.type !== "string" || !VALID_BACKGROUND_TYPES.has(v.type) || typeof v.color !== "string") {
    return undefined;
  }
  const result: WidgetBackgroundStyle = { type: v.type as WidgetBackgroundStyle["type"], color: v.color };
  if (typeof v.color2 === "string") result.color2 = v.color2;
  if (typeof v.angle === "number") result.angle = v.angle;
  if (typeof v.patternId === "string" && VALID_PATTERN_IDS.has(v.patternId)) {
    result.patternId = v.patternId as WidgetBackgroundStyle["patternId"];
  }
  if (typeof v.patternSize === "number") result.patternSize = v.patternSize;
  return result;
}

export class WidgetStyleVisionService {
  /** Analyzes a reference image and proposes EmbedStyle/brand color changes.
   *  Never touches logoUrl — the image is a style reference only. */
  public static async proposeFromImage(imageDataBase64: string): Promise<ProposedWidgetStyle | null> {
    try {
      let timeoutId: number | undefined;
      const { data, error } = await Promise.race([
        supabase.functions.invoke("analyze-image-vision", {
          body: {
            imageData: imageDataBase64,
            prompt: WidgetStyleVisionPrompt.getPrompt(),
            temperature: 0.3,
          },
        }),
        new Promise<never>((_, reject) => {
          timeoutId = window.setTimeout(() => reject(new Error("Image analysis timed out")), ANALYSIS_TIMEOUT_MS);
        }),
      ]);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);

      if (error) {
        console.error("[WidgetStyleVisionService] analyze-image-vision error:", error);
        return null;
      }

      const rawText = typeof data?.response === "string" ? data.response : "";
      if (!rawText) {
        console.warn("[WidgetStyleVisionService] Unexpected response shape:", data);
        return null;
      }

      const jsonMatch = rawText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      const cleanJson = jsonMatch ? jsonMatch[1].trim() : rawText.trim();

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(cleanJson);
      } catch {
        console.error("[WidgetStyleVisionService] Vision response was not valid JSON:", rawText);
        return null;
      }

      const topLevel: ProposedTopLevelBrand = {};
      const embedStyle: ProposedEmbedStyle = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (TOP_LEVEL_KEYS.has(key)) {
          (topLevel as Record<string, unknown>)[key] = value;
        } else if (key === "widgetTemplate") {
          if (typeof value === "string" && VALID_WIDGET_TEMPLATES.has(value as WidgetTemplateId)) {
            embedStyle.widgetTemplate = value as WidgetTemplateId;
          }
        } else if (key === "background" || key === "fullPageBackground") {
          const sanitized = sanitizeBackground(value);
          if (sanitized) (embedStyle as Record<string, unknown>)[key] = sanitized;
        } else if (EMBED_STYLE_KEYS.has(key)) {
          (embedStyle as Record<string, unknown>)[key] = value;
        }
      }

      if (Object.keys(topLevel).length === 0 && Object.keys(embedStyle).length === 0) {
        return null;
      }

      return { topLevel, embedStyle };
    } catch (err) {
      console.error("[WidgetStyleVisionService] Failed to analyze image:", err);
      return null;
    }
  }
}
